import { createRoot } from "react-dom/client";
import { H } from "highlight.run";
import App from "./App.tsx";
import "./index.css";
import { HIGHLIGHT_CONFIG, BUILD_CONFIG } from "./lib/config";
import { LanguageProvider } from "./contexts/LanguageContext";
import { QueryClient } from "@tanstack/react-query";
import { initWebVitals } from "./lib/webVitals";

// Initialize Highlight BEFORE rendering (disabled by default until consent)
const HIGHLIGHT_INGEST = HIGHLIGHT_CONFIG.useEU 
  ? 'https://pub.highlight.run'  // EU endpoint (Frankfurt)
  : 'https://pub.highlight.io';  // US endpoint

H.init(HIGHLIGHT_CONFIG.projectId, {
  environment: HIGHLIGHT_CONFIG.environment,
  version: BUILD_CONFIG.gitSha,
  
  // EU ingest endpoint for GDPR compliance
  backendUrl: HIGHLIGHT_INGEST,
  
  // Privacy: strict masking - no PII captured
  privacySetting: "strict",
  
  // Disable full session replay - only capture on errors
  enableCanvasRecording: false,
  enablePerformanceRecording: false,
  
  // Performance monitoring
  tracingOrigins: [window.location.origin, "https://*.supabase.co"],
  networkRecording: {
    enabled: true,
    recordHeadersAndBody: false,
    urlBlocklist: [
      'auth/password',
      '/api/secret',
    ],
  },
  
  // Console logs for debugging (errors + warnings only in prod)
  consoleMethodsToRecord: import.meta.env.PROD ? ['error', 'warn'] : [],
  
  // Disable by default until user consents (kill-switch via config)
  disableSessionRecording: !HIGHLIGHT_CONFIG.enabled,
});

// Initialize React Query
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

// Register Service Worker for PWA with update detection
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(registration => {
      console.log('[SW] Registered successfully:', registration.scope);
      
      // Check for updates periodically (every 60 seconds)
      setInterval(() => registration.update(), 60000);
      
      // Detect when new SW is installed
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New version available - dispatch custom event for UpdateNotification
            console.log('[SW] New version available');
            window.dispatchEvent(new CustomEvent('sw:update-available'));
          }
        });
      });
    }).catch(error => {
      console.log('[SW] Registration failed:', error);
    });
  });
}

// Initialize Core Web Vitals monitoring
initWebVitals();

createRoot(document.getElementById("root")!).render(
  <LanguageProvider>
    <App />
  </LanguageProvider>
);
